import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _assertThisInitialized from "@babel/runtime/helpers/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/inherits";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }
function _isNativeReflectConstruct() { try { var t = !Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); } catch (t) {} return (_isNativeReflectConstruct = function _isNativeReflectConstruct() { return !!t; })(); }
/* eslint-disable @atlaskit/design-system/consistent-css-prop-usage */
/** @jsx jsx */
import React from 'react';
import { jsx } from '@emotion/react';
import debounce from 'lodash/debounce';
import rafSchd from 'raf-schd';
import { injectIntl } from 'react-intl-next';
import { TRIGGER_METHOD } from '@atlaskit/editor-common/analytics';
import { findReplaceMessages as messages } from '@atlaskit/editor-common/messages';
import { Label } from '@atlaskit/form';
import EditorCloseIcon from '@atlaskit/icon/glyph/editor/close';
import MatchCaseIcon from '@atlaskit/icon/glyph/emoji/keyboard';
import ChevronDownIcon from '@atlaskit/icon/glyph/hipchat/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/glyph/hipchat/chevron-up';
import { getBooleanFF } from '@atlaskit/platform-feature-flags';
import Textfield from '@atlaskit/textfield';
import { FindReplaceTooltipButton } from './FindReplaceTooltipButton';
import { afterInputSection, countStyles, countStylesAlternateStyles, countWrapperStyles, matchCaseSection, sectionWrapperStyles, sectionWrapperStylesAlternate, textFieldWrapper } from './styles';
export var FIND_DEBOUNCE_MS = 100;
// eslint-disable-next-line @repo/internal/react/no-class-components
var Find = /*#__PURE__*/function (_React$Component) {
  _inherits(Find, _React$Component);
  var _super = _createSuper(Find);
  function Find(props) {
    var _this;
    _classCallCheck(this, Find);
    _this = _super.call(this, props);
    _defineProperty(_assertThisInitialized(_this), "findTextfieldRef", /*#__PURE__*/React.createRef());
    _defineProperty(_assertThisInitialized(_this), "isComposing", false);
    _defineProperty(_assertThisInitialized(_this), "syncFindText", function (onSynced) {
      var _this$state;
      // If the external prop findText changes and we aren't in a composition we should update to
      // use the external prop value.
      //
      // An example of where this may happen is when a find occurs through the user selecting some text
      // and pressing Mod-f.
      if (!_this.isComposing && _this.props.findText !== ((_this$state = _this.state) === null || _this$state === void 0 ? void 0 : _this$state.localFindText)) {
        _this.updateFindValue(_this.props.findText || '', onSynced);
      }
    });
    _defineProperty(_assertThisInitialized(_this), "focusFindTextfield", function () {
      var input = _this.findTextfieldRef.current;
      if (_this.props.shouldFocus && input) {
        input.select();
      }
    });
    _defineProperty(_assertThisInitialized(_this), "handleFindChange", function (event) {
      _this.updateFindValue(event.target.value);
    });
    // debounce (vs throttle) to not block typing inside find input while onFind runs
    _defineProperty(_assertThisInitialized(_this), "debouncedFind", debounce(function (value) {
      _this.props.onFind(value);
    }, FIND_DEBOUNCE_MS));
    _defineProperty(_assertThisInitialized(_this), "updateFindValue", function (value, onSynced) {
      _this.setState({
        localFindText: value
      }, function () {
        if (_this.isComposing) {
          return;
        }
        onSynced && onSynced();
        _this.debouncedFind(value);
      });
      _this.props.setFindTyped(true);
    });
    // throtlle between animation frames gives better experience on Enter compared to arbitrary value
    // it adjusts based on performance (and document size)
    _defineProperty(_assertThisInitialized(_this), "handleFindKeyDownThrottled", rafSchd(function (event) {
      if (event.key === 'Enter') {
        if (event.shiftKey) {
          _this.props.onFindPrev({
            triggerMethod: TRIGGER_METHOD.KEYBOARD
          });
        } else {
          _this.props.onFindNext({
            triggerMethod: TRIGGER_METHOD.KEYBOARD
          });
        }
      } else if (event.key === 'ArrowDown') {
        // we want to move focus between find & replace texfields when user hits up/down arrows
        _this.props.onArrowDown();
      }
    }));
    _defineProperty(_assertThisInitialized(_this), "handleFindKeyDown", function (event) {
      if (_this.isComposing) {
        return;
      }
      event.persist();
      _this.handleFindKeyDownThrottled(event);
    });
    _defineProperty(_assertThisInitialized(_this), "handleFindKeyUp", function () {
      _this.handleFindKeyDownThrottled.cancel();
    });
    _defineProperty(_assertThisInitialized(_this), "handleFindNextClick", function (ref) {
      if (_this.isComposing) {
        return;
      }
      _this.props.onFindNext({
        triggerMethod: TRIGGER_METHOD.BUTTON
      });
    });
    _defineProperty(_assertThisInitialized(_this), "handleFindPrevClick", function (ref) {
      if (_this.isComposing) {
        return;
      }
      _this.props.onFindPrev({
        triggerMethod: TRIGGER_METHOD.BUTTON
      });
    });
    _defineProperty(_assertThisInitialized(_this), "handleCompositionStart", function () {
      _this.isComposing = true;
    });
    _defineProperty(_assertThisInitialized(_this), "handleCompositionEnd", function (event) {
      _this.isComposing = false;
      // type for React.CompositionEvent doesn't set type for target correctly
      _this.updateFindValue(event.target.value);
    });
    _defineProperty(_assertThisInitialized(_this), "clearSearch", function () {
      _this.props.onCancel({
        triggerMethod: TRIGGER_METHOD.BUTTON
      });
    });
    _defineProperty(_assertThisInitialized(_this), "handleMatchCaseClick", function (buttonRef) {
      if (_this.props.onToggleMatchCase) {
        _this.props.onToggleMatchCase();
        _this.props.onFind(_this.props.findText);
      }
    });
    var formatMessage = props.intl.formatMessage;
    _this.find = formatMessage(messages.find);
    _this.closeFindReplaceDialog = formatMessage(messages.closeFindReplaceDialog);
    _this.noResultsFound = formatMessage(messages.noResultsFound);
    _this.findNext = formatMessage(messages.findNext);
    _this.findPrevious = formatMessage(messages.findPrevious);
    _this.matchCase = formatMessage(messages.matchCase);
    _this.matchCaseIcon = jsx(MatchCaseIcon, {
      label: _this.matchCase,
      size: getBooleanFF('platform.editor.a11y-find-replace') ? 'small' : 'medium'
    });
    _this.findNextIcon = jsx(ChevronDownIcon, {
      label: _this.findNext
    });
    _this.findPrevIcon = jsx(ChevronUpIcon, {
      label: _this.findPrevious
    });
    _this.closeIcon = jsx(EditorCloseIcon, {
      label: _this.closeFindReplaceDialog
    });

    // We locally manage the value of the input inside this component in order to support compositions.
    // This requires some additional work inside componentDidUpdate to ensure we support changes that
    // occur to this value which do not originate from this component.
    _this.state = {
      localFindText: ''
    };
    return _this;
  }
  _createClass(Find, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      var _this2 = this;
      this.props.onFindTextfieldRefSet(this.findTextfieldRef);

      // focus initially on dialog mount if there is no find text provided
      if (!this.props.findText) {
        this.focusFindTextfield();
      }
      this.syncFindText(function () {
        // focus after input is synced if find text provided
        if (_this2.props.findText) {
          _this2.focusFindTextfield();
        }
      });
    }
  }, {
    key: "componentDidUpdate",
    value: function componentDidUpdate(prevProps) {
      var _this$state2,
        _this3 = this;
      // focus on update if find text did not change
      if (this.props.findText === ((_this$state2 = this.state) === null || _this$state2 === void 0 ? void 0 : _this$state2.localFindText)) {
        this.focusFindTextfield();
      }
      if (this.props.findText !== prevProps.findText) {
        this.syncFindText(function () {
          // focus after input is synced if find text provided
          if (_this3.props.findText) {
            _this3.focusFindTextfield();
          }
        });
      }
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      this.debouncedFind.cancel();
      this.handleFindKeyDownThrottled.cancel();
    }
  }, {
    key: "render",
    value: function render() {
      var _this$props = this.props,
        findText = _this$props.findText,
        count = _this$props.count,
        shouldMatchCase = _this$props.shouldMatchCase,
        formatMessage = _this$props.intl.formatMessage;
      var resultsCount = formatMessage(messages.resultsCount, {
        selectedMatchPosition: count.index + 1,
        totalResultsCount: count.total
      });
      if (getBooleanFF('platform.editor.a11y-find-replace')) {
        var elemAfterInput = jsx("div", {
          css: afterInputSection
        }, jsx("div", {
          "aria-live": "polite"
        }, findText && jsx("span", {
          "data-testid": "textfield-count",
          css: [countStyles, countStylesAlternateStyles]
        }, count.total === 0 ? this.noResultsFound : resultsCount)), jsx("div", {
          css: matchCaseSection
        }, jsx(FindReplaceTooltipButton, {
          title: this.matchCase,
          appearance: "default",
          icon: this.matchCaseIcon,
          newIcon: MatchCaseIcon,
          iconLabel: this.matchCase,
          iconSize: getBooleanFF('platform.editor.a11y-find-replace') ? 'small' : undefined,
          onClick: this.handleMatchCaseClick,
          isPressed: shouldMatchCase
        })));
        return jsx("div", {
          css: [sectionWrapperStyles, sectionWrapperStylesAlternate]
        }, jsx("div", {
          css: textFieldWrapper
        }, jsx(Label, {
          htmlFor: "find-text-field"
        }, this.find), jsx(Textfield, {
          name: "find",
          id: "find-text-field",
          appearance: "standard",
          value: this.state.localFindText,
          ref: this.findTextfieldRef,
          autoComplete: "off",
          onChange: this.handleFindChange,
          onKeyDown: this.handleFindKeyDown,
          onKeyUp: this.handleFindKeyUp,
          onBlur: this.props.onFindBlur,
          onCompositionStart: this.handleCompositionStart,
          onCompositionEnd: this.handleCompositionEnd,
          elemAfterInput: elemAfterInput
        })));
      } else {
        return jsx("div", {
          css: sectionWrapperStyles
        }, jsx(Textfield, {
          name: "find",
          appearance: "none",
          placeholder: this.find,
          value: this.state.localFindText,
          ref: this.findTextfieldRef,
          autoComplete: "off",
          onChange: this.handleFindChange,
          onKeyDown: this.handleFindKeyDown,
          onKeyUp: this.handleFindKeyUp,
          onBlur: this.props.onFindBlur,
          onCompositionStart: this.handleCompositionStart,
          onCompositionEnd: this.handleCompositionEnd
        }), jsx("div", {
          css: countWrapperStyles,
          "aria-live": "polite"
        }, findText && jsx("span", {
          "data-testid": "textfield-count",
          css: countStyles
        }, count.total === 0 ? this.noResultsFound : resultsCount)), jsx(FindReplaceTooltipButton, {
          title: this.matchCase,
          icon: this.matchCaseIcon,
          newIcon: MatchCaseIcon,
          iconLabel: this.matchCase,
          iconSize: getBooleanFF('platform.editor.a11y-find-replace') ? 'small' : undefined,
          onClick: this.handleMatchCaseClick,
          isPressed: shouldMatchCase
        }), jsx(FindReplaceTooltipButton, {
          title: this.findNext,
          icon: this.findNextIcon,
          newIcon: ChevronDownIcon,
          iconLabel: this.findNext,
          keymapDescription: 'Enter',
          onClick: this.handleFindNextClick,
          disabled: count.total <= 1
        }), jsx(FindReplaceTooltipButton, {
          title: this.findPrevious,
          icon: this.findPrevIcon,
          newIcon: ChevronUpIcon,
          iconLabel: this.findPrevious,
          keymapDescription: 'Shift Enter',
          onClick: this.handleFindPrevClick,
          disabled: count.total <= 1
        }), jsx(FindReplaceTooltipButton, {
          title: this.closeFindReplaceDialog,
          icon: this.closeIcon,
          newIcon: EditorCloseIcon,
          iconLabel: this.closeFindReplaceDialog,
          keymapDescription: 'Escape',
          onClick: this.clearSearch
        }));
      }
    }
  }]);
  return Find;
}(React.Component);
export default injectIntl(Find);